import React, { useState, useRef, useEffect } from "react";
import { BASE_URL } from "utils/constant";

const DeliveryPaymentFlow = ({ onClose, delivery }) => {
  const [currentStep, setCurrentStep] = useState("delivery");
  const [deliveryData, setDeliveryData] = useState(delivery);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => {
    setCurrentStep("payment");
  };

  const handleBack = () => {
    setCurrentStep("delivery");
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto bg-white rounded-xl shadow-2xl flex flex-col h-[80vh] max-h-[800px]">
      <div className="h-full flex flex-col overflow-hidden rounded-xl">
        <div className="flex-1 overflow-hidden relative">
          <div
            className="flex h-full transition-transform duration-300 ease-in-out"
            style={{
              transform:
                currentStep === "payment" ? "translateX(-100%)" : "translateX(0)",
            }}
          >
            <div className="w-full flex-shrink-0 h-full">
              <MaterialDeliveryCheck
                onComplete={handleNext}
                onClose={onClose}
                deliveryData={deliveryData}
                setDeliveryData={setDeliveryData}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
              />
            </div>
            <div className="w-full flex-shrink-0 h-full">
              <PaymentDetails
                onClose={onClose}
                onBack={handleBack}
                deliveryData={deliveryData}
                setDeliveryData={setDeliveryData}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeCheckValue = (value) => {
  if (!value) return "";
  const v = String(value).trim().toLowerCase();
  if (v === "matched") return "matched";
  if (v === "fault") return "fault";
  // Backward compatibility for older values
  if (v === "match" || v === "ok") return "matched";
  return v;
};

const getMaterialOverallStatus = (material) => {
  const spec = normalizeCheckValue(material?.specification_check);
  const qty = normalizeCheckValue(material?.quantity_check);
  const qual = normalizeCheckValue(material?.quality_check);

  if (spec === "fault" || qty === "fault" || qual === "fault") return "fault";
  if (spec === "matched" && qty === "matched" && qual === "matched")
    return "matched";
  return "unset";
};

const MaterialDeliveryCheck = ({
  onComplete,
  onClose,
  deliveryData,
  setDeliveryData,
  isSubmitting,
  setIsSubmitting
}) => {
  const updateTimersRef = useRef({});
  const [openFaultForMaterialId, setOpenFaultForMaterialId] = useState(null);
  const [faultDropdownOpenFor, setFaultDropdownOpenFor] = useState(null);
  const faultDropdownWrapperRef = useRef(null);
  const [error, setError] = useState("");
  const handleMarkAsReceived = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");
      // Then mark as delivered
      const response = await fetch(`${BASE_URL}/inventory/mark-as-delivered/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          delivery_id: deliveryData.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to mark as received");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/inventory/update-delivery/${deliveryData.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            transaction_id: 3,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process return");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process return");
    } finally {
      setIsSubmitting(false);
    }
  };
  // Close the fault dropdown when clicking anywhere outside it
  useEffect(() => {
    if (!faultDropdownOpenFor) return;

    const handleClickOutside = (event) => {
      if (
        faultDropdownWrapperRef.current &&
        !faultDropdownWrapperRef.current.contains(event.target)
      ) {
        setFaultDropdownOpenFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [faultDropdownOpenFor]);

  const updateMaterialCheck = (materialId, checkData) => {
    // Optimistically update UI immediately for smooth typing/backspace
    setDeliveryData((prev) => ({
      ...prev,
      all_materials: prev.all_materials.map((material) =>
        material.id === materialId ? { ...material, ...checkData } : material
      ),
    }));

    // Debounce API calls per material to avoid spamming on every keystroke
    if (updateTimersRef.current[materialId]) {
      clearTimeout(updateTimersRef.current[materialId]);
    }

    updateTimersRef.current[materialId] = setTimeout(async () => {
      try {
        const accessToken = localStorage.getItem("access");

        // Sanitize payload (allow empty string -> null; ensure numbers are numbers)
        const payload = { ...checkData };
        if (Object.prototype.hasOwnProperty.call(payload, "false_quantity")) {
          const val = payload.false_quantity;
          payload.false_quantity =
            val === "" || val === null ? null : Number(val);
        }

        const response = await fetch(
          `${BASE_URL}/inventory/update-materials-of-delivery/${materialId}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          console.error("Failed to update material check");
        }
      } catch (error) {
        console.error("Error updating material check:", error);
      } finally {
        delete updateTimersRef.current[materialId];
      }
    }, 300);
  };

  const setMaterialMatched = (materialId) => {
    // "Matched" == mark material as delivered/checked (no faults)
    setOpenFaultForMaterialId(null);
    setFaultDropdownOpenFor(null);
    updateMaterialCheck(materialId, {
      specification_check: "matched",
      quantity_check: "matched",
      quality_check: "matched",
      specification_check_comment: "",
      false_quantity: null,
    });
  };

  const toggleFaultCategory = (material, categoryKey) => {
    const current = normalizeCheckValue(material?.[categoryKey]);
    const nextValue = current === "fault" ? "matched" : "fault";

    const patch = {
      [categoryKey]: nextValue,
    };

    // If quantity fault is being turned off, clear false_quantity
    if (categoryKey === "quantity_check" && nextValue !== "fault") {
      patch.false_quantity = null;
    }

    updateMaterialCheck(material.id, patch);
  };

  const updateFaultDetails = (materialId, value) => {
    // Backend currently supports specification_check_comment; use it for the overall fault details.
    updateMaterialCheck(materialId, {
      specification_check_comment: value,
    });
  };

  const updateFaultyQuantity = (materialId, value) => {
    updateMaterialCheck(materialId, {
      false_quantity: value,
      quantity_check: "fault",
    });
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white z-10">
        <div className="relative flex items-center justify-center">
          <h1 className="text-xl font-bold text-gray-800">Delivery Check</h1>
          <button
            className="absolute right-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            onClick={onClose}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {error && (
          <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100 flex items-center">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
        <div className="space-y-4">
          {deliveryData.all_materials.map((material, index) => (
            <div key={material.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2 pb-2 border-b border-gray-100">
                <div>
                  <h3 className="text-base font-bold text-gray-800">{material.material_name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{material.material_subcategory}</p>
                </div>
                <div className="bg-purple-50 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  #{index + 1}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {/* Quick summary line */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Ordered</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {material.quantity} {material.unit}
                      {/* • ₹ {material.unit_price}/{material.unit} */}
                    </span>
                  </div>

                  {/* Status badge */}
                  {(() => {
                    const st = getMaterialOverallStatus(material);
                    if (st === "matched") {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                          Matched
                        </span>
                      );
                    }
                    if (st === "fault") {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
                          Fault
                        </span>
                      );
                    }
                    return (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-100">
                        Pending
                      </span>
                    );
                  })()}
                </div>

                {/* Two-button flow */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex-1 py-2 rounded-lg font-semibold text-sm border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                    onClick={() => setMaterialMatched(material.id)}
                  >
                    MATCHED
                  </button>
                  <button
                    type="button"
                    className="flex-1 py-2 rounded-lg font-semibold text-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    onClick={() => {
                      setOpenFaultForMaterialId(material.id);
                      // Do not auto-mark fault; user must choose categories below.
                    }}
                  >
                    FAULT
                  </button>
                </div>

                {/* Fault details panel */}
                {openFaultForMaterialId === material.id && (
                  <div
                    className="mt-1 p-3 rounded-lg border border-red-100 bg-red-50/30"
                    onClick={() => {
                      // Clicking within should not close; dropdown handles its own click stop.
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-gray-800">
                        Faulty items
                      </div>
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                        onClick={() => {
                          setOpenFaultForMaterialId(null);
                          setFaultDropdownOpenFor(null);
                        }}
                      >
                        Close
                      </button>
                    </div>

                    <div
                      className="mt-2 relative"
                      ref={
                        faultDropdownOpenFor === material.id
                          ? faultDropdownWrapperRef
                          : null
                      }
                    >
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-gray-200 bg-white text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFaultDropdownOpenFor((prev) =>
                            prev === material.id ? null : material.id
                          );
                        }}
                      >
                        <span className="text-gray-700">
                          Select issues (Specification / Quantity / Quality)
                        </span>
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {faultDropdownOpenFor === material.id && (
                        <div
                          className="absolute top-full left-0 mt-1 w-full rounded-md border bg-white shadow-lg z-20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {[
                            { key: "specification_check", label: "Specification" },
                            { key: "quantity_check", label: "Quantity" },
                            { key: "quality_check", label: "Quality / Damage" },
                          ].map((opt) => {
                            const checked =
                              normalizeCheckValue(material?.[opt.key]) ===
                              "fault";
                            return (
                              <label
                                key={opt.key}
                                className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                              >
                                <span className="text-gray-700">{opt.label}</span>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleFaultCategory(material, opt.key)}
                                  className="h-4 w-4 accent-[#3B0069]"
                                />
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Quantity fault input (only if quantity selected) */}
                    {normalizeCheckValue(material?.quantity_check) === "fault" && (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Faulty / Short Quantity (optional)
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="0"
                            value={material.false_quantity ?? ""}
                            onChange={(e) =>
                              updateFaultyQuantity(material.id, e.target.value)
                            }
                            className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                          />
                          <span className="text-sm text-gray-500">
                            {material.unit}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Details */}
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Fault / Damage details
                      </label>
                      <textarea
                        rows={3}
                        value={material.specification_check_comment || ""}
                        onChange={(e) =>
                          updateFaultDetails(material.id, e.target.value)
                        }
                        placeholder="Describe what’s wrong (damage, mismatch, missing items, etc.)"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        Tip: select at least one issue above so this material is
                        recorded as a fault.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      {/* <div className="flex-shrink-0 border-t border-gray-200 bg-white flex justify-center">
        <div className="w-full max-w-sm">
          <button
            onClick={onComplete}
            className="w-auto px-8 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl flex items-center justify-center font-semibold transition-all shadow-lg shadow-purple-200 hover:shadow-purple-300 mx-auto scale-90"
          >
            Continue to Payment
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </button>
        </div>
      </div> */}
      <div className="flex-shrink-0 border-t py-2 border-gray-200 bg-white">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex gap-3 justify-center">
            <button
              className={`flex-1 bg-green-600 hover:bg-green-600 text-white py-2.5 rounded-lg flex items-center justify-center text-sm font-semibold transition-all shadow-lg shadow-green-200 hover:shadow-green-300 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                } scale-95`}
              onClick={handleMarkAsReceived}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Mark Received"}
              {!isSubmitting && (
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
            <button
              className={`flex-1 bg-white border-2 border-red-100 text-red-600 hover:bg-red-50 py-2.5 rounded-lg flex items-center justify-center text-sm font-semibold transition-all scale-95 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              onClick={handleReturn}
              disabled={isSubmitting}
            >
              Return Items
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentDetails = ({
  onClose,
  onBack,
  deliveryData,
  setDeliveryData,
  isSubmitting,
  setIsSubmitting,
}) => {
  const [newAmount, setNewAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(
    deliveryData.payment_method
  );
  const [error, setError] = useState("");

  const paymentMethods = ["Cash", "UPI/Bank Transfer", "Cheque"];

  const handleMarkAsReceived = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      // If there's a new payment amount, create part payment first
      if (newAmount && Number(newAmount) > 0) {
        const partPaymentResponse = await fetch(
          `${BASE_URL}/inventory/create-partpayment/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              paid_amount: Number(newAmount),
              payment_method: paymentMethod,
              delivery: deliveryData.id,
            }),
          }
        );

        if (!partPaymentResponse.ok) {
          const errorData = await partPaymentResponse.json();
          throw new Error(errorData.message || "Failed to create part payment");
        }
      }

      // Then mark as delivered
      const response = await fetch(`${BASE_URL}/inventory/mark-as-delivered/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          delivery_id: deliveryData.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to mark as received");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/inventory/update-delivery/${deliveryData.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            transaction_id: 3,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process return");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process return");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white z-10">
        <div className="relative flex items-center justify-center mb-4">
          <button
            className="absolute left-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            onClick={onBack}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">Payment Details</h1>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Step 1</span>
          <span className="text-gray-300">/</span>
          <span className="font-medium text-purple-600">Step 2</span>
        </div>
        <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
          <div className="w-full bg-purple-600 h-full rounded-full transition-all duration-300" />
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100 flex items-center">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
        <div className="flex flex-col gap-3">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold mb-3 text-gray-800 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Total Amount</span>
                <span className="font-bold text-gray-900">₹ {deliveryData.total_price}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Paid Amount</span>
                <span className="font-bold text-green-600">₹ {deliveryData.paid_amount}</span>
              </div>
              <div className="h-px bg-gray-100 my-1"></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Payment Due</span>
                <span className="font-bold text-base text-red-600">₹ {deliveryData.remaining_payment}</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold mb-3 text-gray-800 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Add Payment
            </h3>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Amount
                </label>
                <div className="relative w-60">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    className="w-full py-1.5 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all text-gray-900 placeholder-gray-400 text-sm"
                    placeholder="Enter amount"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method}
                      className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${paymentMethod === method
                        ? "bg-purple-50 border-purple-200 text-purple-700 shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                        }`}
                      onClick={() => setPaymentMethod(method)}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>


              <div className="flex items-center text-sm gap-2">
                <span className="text-gray-500">Remaining Balance</span>
                <span className="font-bold text-gray-900">
                  ₹ {Math.max(0, deliveryData.remaining_payment - (Number(newAmount) || 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex gap-3 justify-center">
            <button
              className={`flex-1 bg-green-600 hover:bg-green-600 text-white py-2.5 rounded-xl flex items-center justify-center text-sm font-semibold transition-all shadow-lg shadow-green-200 hover:shadow-green-300 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                } scale-95`}
              onClick={handleMarkAsReceived}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Mark Received"}
              {!isSubmitting && (
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
            <button
              className={`flex-1 bg-white border-2 border-red-100 text-red-600 hover:bg-red-50 py-2.5 rounded-xl flex items-center justify-center text-sm font-semibold transition-all scale-95 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              onClick={handleReturn}
              disabled={isSubmitting}
            >
              Return Items
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryPaymentFlow;
